<template>
  <input
    ref="inputRef"
    v-model="modelValue"
    type="text"
    class="ui-input-string"
    :style="mainStyles"
    :disabled
    @keydown.enter="handleEnter"
    @blur="handleSubmit"
    @click.stop
  >
  <div
    ref="textRef"
    class="ui-input-string__text"
    v-html="textValue"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, useTemplateRef, ref } from 'vue'
import DOMPurify from 'dompurify'
import { useElementBounding } from '@vueuse/core'

type Emits = {
  save: [data: string, callback: () => void]
}

const emit = defineEmits<Emits>()

const modelValue = defineModel<string>({ required: true })

const inputRef = useTemplateRef('inputRef')
const textRef = useTemplateRef('textRef')

const { width } = useElementBounding(textRef)

const disabled = ref(false)

const textValue = computed(() => {
  return DOMPurify.sanitize(modelValue.value.replace(/ /g, '&nbsp;'))
})

const mainStyles = computed(() => ({
  width: `${width.value}px`,
}))

const handleEnter = () => {
  inputRef.value?.blur()
}

const handleSubmit = () => {
  disabled.value = true
  emit('save', modelValue.value, () => {
    disabled.value = false
  })
}

onMounted(async () => {
  await nextTick()
  inputRef.value?.focus()
})
</script>

<style>
.ui-input-string {
  &:is(input) {
    @apply min-w-[1ch];
    @apply p-0;
    @apply bg-transparent;
    @apply border-none !ring-0;
    @apply shadow-none;
    @apply text-base sm:text-sm;
  }

  &__text {
    @apply absolute;
    @apply invisible;
    @apply text-base sm:text-sm;
    @apply pointer-events-none;
  }
}
</style>
